import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';
import { formatIarRunNumber } from '@/utils/iarRunNumber';
import type {
  IARCompleteRunResponse,
  IARGetRunResponse,
  IARGetRunPrintResponse,
  IARInitiateRunResponse,
  IARListRunsResponse,
  IARRetryDiscrepancyResponse,
  IARRunCompletionArtifacts,
  IARRunDetail,
  IARRunLine,
  IARRunSummary,
  IARRunTotals,
} from '@/types/iar';

interface BackendIARRunSummary {
  id?: string;
  runId?: string;
  status?: string;
  startedAt?: string | null;
  createdAt?: string | null;
  runDate?: string;
  inventoriedQtyTotal?: number;
  inventoryQty?: number;
  runNumber?: string;
}

interface BackendIARInitiateRunResponse {
  runId?: string;
  id?: string;
  run?: {
    id?: string;
  };
}

interface BackendIARRunMutationResponse {
  run?: IARRunDetail;
  lines?: IARRunLine[];
  totals?: IARRunTotals;
  artifacts?: IARRunCompletionArtifacts | null;
}

const toIARRunSummary = (run: BackendIARRunSummary): IARRunSummary => {
  const runId = run.runId ?? run.id ?? '';
  const runDate = run.runDate ?? run.startedAt ?? run.createdAt ?? '';
  const normalizedRunNumber = run.runNumber?.trim();

  return {
    runId,
    runNumber:
      normalizedRunNumber && normalizedRunNumber.length > 0
        ? normalizedRunNumber
        : formatIarRunNumber({ runId, runDate }),
    runDate,
    status: (run.status as IARRunSummary['status']) ?? 'In Progress',
    inventoryQty: run.inventoryQty ?? run.inventoriedQtyTotal ?? 0,
  };
};

const toRunMutationResponse = (response: BackendIARRunMutationResponse): IARGetRunResponse => {
  const run = response?.run;
  if (!run) {
    throw new Error('IAR run response is missing run metadata');
  }

  return {
    run,
    lines: response?.lines ?? [],
    totals: response?.totals ?? {
      recordedQtyTotal: 0,
      inventoriedQtyTotal: 0,
      gainQtyTotal: 0,
      lossQtyTotal: 0,
    },
    artifacts: response?.artifacts ?? null,
  };
};

export class IARService {
  async listRuns(): Promise<IARRunSummary[]> {
    const response = await httpClient.get<IARListRunsResponse | BackendIARRunSummary[]>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs`,
    );

    if (Array.isArray(response)) {
      return response.map((run) => toIARRunSummary(run)).filter((run) => run.runId.length > 0);
    }

    return (response?.runs ?? [])
      .map((run) => toIARRunSummary(run))
      .filter((run) => run.runId.length > 0);
  }

  async initiateRun(): Promise<IARInitiateRunResponse> {
    const response = await httpClient.post<BackendIARInitiateRunResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs`,
      {},
    );

    const runId = response?.runId ?? response?.id ?? response?.run?.id;
    if (!runId) {
      throw new Error('Unable to determine IAR run ID from initiate response');
    }

    return { runId };
  }

  async cancelRun(runId: string): Promise<void> {
    await httpClient.post<void>(`${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/cancel`);
  }

  async getRun(runId: string): Promise<IARGetRunResponse> {
    const response = await httpClient.get<BackendIARRunMutationResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}`,
    );

    return toRunMutationResponse(response);
  }

  async saveRunLines(
    runId: string,
    lines: Array<{
      id: string;
      inventoriedQty: number | null;
    }>,
  ): Promise<IARGetRunResponse> {
    const response = await httpClient.patch<BackendIARRunMutationResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/lines`,
      { lines },
    );

    return toRunMutationResponse(response);
  }

  async refreshRun(runId: string): Promise<IARGetRunResponse> {
    const response = await httpClient.post<BackendIARRunMutationResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/refresh`,
    );

    return toRunMutationResponse(response);
  }

  async getPrintPayload(runId: string): Promise<IARGetRunPrintResponse> {
    const response = await httpClient.get<IARGetRunPrintResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/print`,
    );

    const run = response?.run;
    if (!run) {
      throw new Error('IAR print payload is missing run metadata');
    }

    const rows = response?.rows ?? [];
    const fallbackTotals = {
      lineCount: rows.length,
      recordedQtyTotal: rows.reduce((sum, row) => sum + row.recordedQty, 0),
    };

    return {
      run,
      generatedAt: response?.generatedAt ?? new Date().toISOString(),
      rows,
      totals: response?.totals ?? fallbackTotals,
    };
  }

  async completeRun(runId: string): Promise<IARCompleteRunResponse> {
    const response = await httpClient.post<IARCompleteRunResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/complete`,
      undefined,
      { timeout: 30_000 },
    );

    const run = response?.run;
    if (!run) {
      throw new Error('IAR completion response is missing run metadata');
    }

    return {
      run,
      artifacts: response?.artifacts ?? null,
      discrepancyWorkflow: response?.discrepancyWorkflow ?? null,
    };
  }

  async retryDiscrepancy(runId: string): Promise<IARRetryDiscrepancyResponse> {
    const response = await httpClient.post<IARRetryDiscrepancyResponse>(
      `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs/${runId}/retry-discrepancy`,
      undefined,
      { timeout: 30_000 },
    );

    const run = response?.run;
    if (!run) {
      throw new Error('IAR retry response is missing run metadata');
    }

    return {
      run,
      discrepancyWorkflow: response.discrepancyWorkflow,
    };
  }
}

export const iarService = new IARService();
